import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import axios, { AxiosInstance } from 'axios';
import Routes from 'shared/enums/routes';

export type AxiosClients = {
  authenticatedInstance: AxiosInstance;
  unauthenticatedInstance: AxiosInstance;
  notificationProcessorInstance: AxiosInstance;
};

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';
const notificationProcessorApiBaseURL = import.meta.env.VITE_NOTIFICATION_PROCESSOR_API_BASE_URL ?? '';
const unauthorizedHttpStatusCode = 401;
const maintenanceHttpStatusCode = 999;

const useAxiosClients = (): AxiosClients => {
  const auth = useAuth();

  const authenticatedInstance = useRef(
    axios.create({
      baseURL,
    }),
  );
  const unauthenticatedInstance = useRef(
    axios.create({
      baseURL,
    }),
  );
  const notificationProcessorInstance = useRef(
    axios.create({
      baseURL: notificationProcessorApiBaseURL,
    }),
  );

  const updateAuthHeader = (accessToken: string | undefined, instance: AxiosInstance): void => {
    if (accessToken) {
      instance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      delete instance.defaults.headers.common.Authorization;
    }
  };

  useEffect(() => {
    updateAuthHeader(auth.user?.access_token, authenticatedInstance.current);
    updateAuthHeader(auth.user?.access_token, notificationProcessorInstance.current);
  }, [auth.user?.access_token]);

  const logout = (isMaintenance: boolean): void => {
    if (
      auth.user?.id_token &&
      !globalThis.location.pathname.includes('login') &&
      !globalThis.location.pathname.includes('signout-redirect')
    ) {
      auth.stopSilentRenew();
      if (!isMaintenance) {
        auth.signoutRedirect({
          id_token_hint: auth.user?.id_token,
          post_logout_redirect_uri: import.meta.env.VITE_OIDC_SIGNOUT_REDIRECT_URI,
        });
      }

      auth.removeUser();

      if (isMaintenance) {
        globalThis.location.href = Routes.Login;
      }
    }
  };

  authenticatedInstance.current.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response.status === unauthorizedHttpStatusCode || error.response.status === maintenanceHttpStatusCode) {
        logout(error.response.status === maintenanceHttpStatusCode);
      }
      throw error;
    },
  );

  notificationProcessorInstance.current.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response.status === unauthorizedHttpStatusCode || error.response.status === maintenanceHttpStatusCode) {
        logout(error.response.status === maintenanceHttpStatusCode);
      }
      throw error;
    },
  );

  authenticatedInstance.current.defaults.headers.post['Content-Type'] = 'application/json';
  unauthenticatedInstance.current.defaults.headers.post['Content-Type'] = 'application/json';
  notificationProcessorInstance.current.defaults.headers.post['Content-Type'] = 'application/json';
  authenticatedInstance.current.defaults.headers.put['Content-Type'] = 'application/json';
  unauthenticatedInstance.current.defaults.headers.put['Content-Type'] = 'application/json';
  notificationProcessorInstance.current.defaults.headers.put['Content-Type'] = 'application/json';
  updateAuthHeader(auth.user?.access_token, authenticatedInstance.current);
  updateAuthHeader(auth.user?.access_token, notificationProcessorInstance.current);

  return {
    authenticatedInstance: authenticatedInstance.current,
    unauthenticatedInstance: unauthenticatedInstance.current,
    notificationProcessorInstance: notificationProcessorInstance.current,
  };
};

export default useAxiosClients;
